import React from 'react';
import styled from 'styled-components';
import type { FreePhotoMetadata } from '../utils/freePhotoUtils';
import { formatCompassHeading } from '../utils/freePhotoUtils';
import geocollectLogo from '~/assets/images/geocloud_logo.png';

interface FreePhotoStampOverlayProps {
  metadata: FreePhotoMetadata | null;
  createdAt?: string;
  /** App label shown top-left. Default: "GeoCollect" */
  appName?: string;
  /** Optional className for the root wrapper */
  className?: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

const DAYS = ['Chủ nhật', 'Thứ hai', 'Thứ ba', 'Thứ tư', 'Thứ năm', 'Thứ sáu', 'Thứ bảy'];

function formatDateTime(isoString?: string) {
  const d = isoString ? new Date(isoString) : new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`,
    dayOfWeek: DAYS[d.getDay()],
  };
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Overlay div that mimics the stamp layout from GeoCollect CanvasUtils.ts.
 * Place this as a positioned child (absolute) over the photo container.
 *
 * Layout (matches geocollect):
 *   Top-left:    [icon] GeoCollect
 *   Bottom-left (bottom→top):
 *                GPS block: hệ chiếu, tọa độ, sai số, độ cao, hướng chụp
 *                Địa chỉ
 *                Time (large) | yellow bar | Date / Day-of-week
 */
export const FreePhotoStampOverlay: React.FC<FreePhotoStampOverlayProps> = ({
  metadata,
  createdAt,
  appName = 'GeoCollect',
  className,
}) => {
  const lat      = metadata?.location?.coords?.latitude;
  const lon      = metadata?.location?.coords?.longitude;
  const accuracy = metadata?.location?.coords?.accuracy;
  const altitude = metadata?.location?.coords?.altitude;
  const bearing  = metadata?.compassHeading;
  const address  = metadata?.address;
  // Dùng timestamp từ location nếu không có createdAt / capturedAt
  const timestamp = metadata?.location?.timestamp;
  const displayTime = createdAt ?? metadata?.capturedAt ?? (timestamp ? new Date(timestamp).toISOString() : undefined);

  const { time, date, dayOfWeek } = formatDateTime(displayTime);

  // Info lines — same order as geocollect CanvasUtils (top→bottom inside box)
  const infoLines: string[] = [];
  if (lat != null && lon != null) {
    infoLines.push('Hệ chiếu: Lat/Lon WGS84');
    infoLines.push(`${Number(lat).toFixed(7)}, ${Number(lon).toFixed(7)}`);
    infoLines.push(''); // blank separator
  }
  if (accuracy != null) {
    infoLines.push(`Sai số: ${Number(accuracy).toFixed(2)} mét`);
  }
  if (altitude != null) {
    infoLines.push(`Độ cao: ${Number(altitude).toFixed(2)} mét`);
  }
  if (bearing != null) {
    infoLines.push(`Hướng chụp: ${formatCompassHeading(Number(bearing))}`);
  }

  const hasInfo = infoLines.some(l => l !== '');

  return (
    <UI_Root className={className}>
      {/* Top-left: App icon + name */}
      <UI_TopLeft>
        <UI_AppIcon src={geocollectLogo} alt="GeoCollect" />
        <UI_AppName>{appName}</UI_AppName>
      </UI_TopLeft>

      {/* Bottom-left stack (column-reverse so items stack upward from bottom) */}
      <UI_BottomLeft>
        {/* GPS info block — bottom-most */}
        {hasInfo && (
          <UI_InfoBlock>
            {infoLines.map((line, i) =>
              line === '' ? (
                <UI_InfoSpacer key={i} />
              ) : (
                <UI_InfoLine key={i}>{line}</UI_InfoLine>
              ),
            )}
          </UI_InfoBlock>
        )}

        {/* Address — above GPS block */}
        {address && <UI_Address>{address}</UI_Address>}

        {/* Time row — top of bottom stack */}
        <UI_TimeRow>
          <UI_Time>{time}</UI_Time>
          <UI_YellowBar />
          <UI_DateGroup>
            <UI_Date>{date}</UI_Date>
            <UI_DayOfWeek>{dayOfWeek}</UI_DayOfWeek>
          </UI_DateGroup>
        </UI_TimeRow>
      </UI_BottomLeft>
    </UI_Root>
  );
};

// ─── Styled components ────────────────────────────────────────────────────────

/** Root: fills parent, no pointer events so clicks pass through */
const UI_Root = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  font-family: 'Roboto', sans-serif;
`;

// ── Top-left block ────────────────────────────────────────────────────────────

const UI_TopLeft = styled.div`
  position: absolute;
  top: 6px;
  left: 8px;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const UI_AppIcon = styled.img`
  width: 1.4em;
  height: 1.4em;
  object-fit: contain;
  border-radius: 3px;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.7));
`;

const UI_AppName = styled.span`
  color: white;
  font-size: 0.65em;
  font-weight: 700;
  line-height: 1.3;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.85), 0 0 6px rgba(0, 0, 0, 0.6);
  white-space: nowrap;
`;

// ── Bottom-left block ─────────────────────────────────────────────────────────

const UI_BottomLeft = styled.div`
  position: absolute;
  bottom: 6px;
  left: 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
  max-width: 72%;
`;

const UI_InfoBlock = styled.div`
  background: rgba(0, 0, 0, 0.35);
  border-radius: 5px;
  padding: 3px 6px;
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const UI_InfoLine = styled.div`
  color: white;
  font-size: 0.52em;
  line-height: 1.45;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_InfoSpacer = styled.div`
  height: 0.3em;
`;

const UI_Address = styled.div`
  color: white;
  font-size: 0.52em;
  line-height: 1.4;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
  /* allow wrapping for long addresses */
  word-break: break-word;
`;

const UI_TimeRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 5px;
`;

const UI_Time = styled.div`
  color: white;
  font-size: 1.05em;
  font-weight: 700;
  white-space: nowrap;
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.85), 0 0 6px rgba(0, 0, 0, 0.6);
`;

const UI_YellowBar = styled.div`
  width: 2px;
  align-self: stretch;
  background-color: #ffce00;
  border-radius: 1px;
  flex-shrink: 0;
  /* small vertical margin so bar doesn't touch edges */
  margin: 1px 0;
`;

const UI_DateGroup = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 1px;
`;

const UI_Date = styled.div`
  color: white;
  font-size: 0.52em;
  line-height: 1.3;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;

const UI_DayOfWeek = styled.div`
  color: white;
  font-size: 0.52em;
  line-height: 1.3;
  white-space: nowrap;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.8);
`;
